/**
 * Tile-delta → screen-space facing for the 8-dir sprite set (M2 lab).
 *
 * The iso projection maps tile deltas to screen diagonals: sx = dx − dy,
 * sy = dx + dy (iso.ts world→screen). The sprite dirs are named in SCREEN
 * space (n = up-screen, e = right-screen, …). Pure integer-ish math
 * (atan2 is fine here — client render code, not the sim): the angle of the
 * screen vector is quantized to the nearest of the 8 dirs, n = 0 going
 * clockwise.
 */
import type { Dir } from "./atlas.js";

export type { Dir } from "./atlas.js";

export const DIRS_CW: readonly Dir[] = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];

/**
 * Facing for a movement delta (dx, dy) in tile coords, or null for a zero
 * delta. Tile deltas in this 2:1 projection:
 *   +x tile → se (screen down-right), +y tile → sw (screen down-left).
 */
export function tileDeltaToDir(dx: number, dy: number): Dir | null {
  if (dx === 0 && dy === 0) return null;
  const sx = dx - dy;
  const sy = dx + dy;
  const angle = Math.atan2(sy, sx);
  // 0 → n, 1 → ne, … 7 → nw (clockwise from n)
  const idx = Math.round((angle + Math.PI / 2) / (Math.PI / 4));
  const i = ((idx % 8) + 8) % 8;
  return DIRS_CW[i] ?? "s"; // i is always 0..7; fallback for TS
}
